
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { ProrationRule } from 'nomst/Stores/Configuration/prorationRule';
import { IConfigurationDataManager } from 'typings/dataManagers';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { observable, computed, action, makeObservable } from 'mobx';
import { IProrationRule } from 'typings/prorationRule';
import { getActiveConfiguration } from 'nomst/Actions/configurations';

export class ProrationRuleTabView {
    @observable prorationRules: ProrationRule[];
    @observable isLoading: boolean;
    configurationDataManager: IConfigurationDataManager;
    masterAccountId: string;

    constructor(currectConfig: Configuration) {
        makeObservable(this);
        this.prorationRules = [];
        this.configurationDataManager = dataManagerFactory.getConfigurationDataManager();
        this.masterAccountId = currectConfig.masterAccountId;

        this.removeRule = this.removeRule.bind(this);
        this.updateAssociatedAmounts = this.updateAssociatedAmounts.bind(this);
    }

    @computed
    get currentConfiguration(): Configuration {
        return getActiveConfiguration();
    }

    @computed
    get doesHaveBudget(): boolean {
        const budgets = this.currentConfiguration.cycleBudgets;
        return budgets && budgets.length > 0;
    }

    @computed
    get anyRulesInEditMode(): boolean {
        return this.prorationRules.some(rule => rule.isEditMode);
    }
    
    @action
    async fetchRules(): Promise<void> {
        if (this.isLoading) {
            return;
        }
        this.isLoading = true;
        this.prorationRules = [];
        const rules = await this.configurationDataManager.getProrationRules(
            this.masterAccountId,
            this.currentConfiguration.cycleId);

        rules?.forEach(rule => {
            this.prorationRules.push(new ProrationRule(rule));
        });

        this.prorationRules.forEach(rule => rule.setAssociatedCycleAmountsTitle());
        this.isLoading = false;
    }

    @action
    addNewRule(): void {
        const prorationRule = new ProrationRule({} as IProrationRule);
        this.prorationRules.push(prorationRule);
        prorationRule.editRule();
    }

    @action
    updateAssociatedAmounts(prorationRule: ProrationRule): void {
        const associatedAmounts = new Set(prorationRule.associatedAmountsTitle);
        const otherRules = this.prorationRules.filter(rule =>
            rule.prorationRuleId !== prorationRule.prorationRuleId);
        otherRules.forEach(rule => {
            rule.associatedAmountsTitle = rule.associatedAmountsTitle.filter(amount => !associatedAmounts.has(amount));
        });

        // remove any rules that no longer have associated amounts
        this.prorationRules = otherRules.filter(rule => rule.associatedAmountsTitle.length > 0).concat(prorationRule);
    }

    @action
    removeRule(prorationRule: ProrationRule): void {
        const ruleIndex = this.prorationRules.findIndex(rule => rule === prorationRule);
        this.prorationRules.splice(ruleIndex, 1);
    }

    @action
    async deleteRule(prorationRule: ProrationRule): Promise<void> {
        if (prorationRule.prorationRuleId) {
            this.isLoading = true;
            prorationRule.currentAssociatedCycleAmounts.forEach(amount =>
                amount.deleteProrationRule());

            await this.configurationDataManager.deleteProrationRule(
                this.masterAccountId,
                this.currentConfiguration.cycleId,
                prorationRule.prorationRuleId);

            this.isLoading = false;
        }

        this.removeRule(prorationRule);
    }
}